import React, { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import AgendaHeader from './views/agendaHeader';
import { Select, TabPanel, Tabs, Typography, Option } from '@getgo/chameleon-web/react';
import AgendaDetail from './views/agendaDetail';
import { FilterConferenceOptions } from './agendaUtils';
import { getFavorites } from '../hooks/useFavoriteIconHook';
import { IEventDetailDate, IAgendaConference, IEventDetail } from '../event/eventInterface';
import { IUserDetail } from '../context/userContext';
import './styles/agenda.scss';
import './styles/favorite.scss';
import { isToday } from '../utils/eventDataFormat';
import { useTranslation } from 'react-i18next';
import {
  onClickOfEventFilterTracker,
  onClickOfEventDateTabTracker,
} from '../../mixpanel/Feature/eventTracker';

export interface AgendaProps {
  eventDetail: IEventDetail;
  handleEventSpeakerChange: (index: number, speakerId: string) => void;
  userDetail: IUserDetail;
  isMobOrTabView: boolean;
}

export interface OptionsStatusDisabled {
  favorite: boolean;
  ended: boolean;
  upcoming: boolean;
  ongoing: boolean;
}

const Agenda: FC<AgendaProps> = ({
  eventDetail,
  handleEventSpeakerChange,
  userDetail,
  isMobOrTabView,
}) => {
  const { t } = useTranslation();
  const SelectRef = useRef<any>();

  const [todayIndex, activeId, allConferences] = useMemo(() => {
    const index = eventDetail?.eventDetailDates.findIndex((eventDetailDate: IEventDetailDate) =>
      isToday(new Date(eventDetailDate.eventDate)),
    );

    const currentActiveId = String(index !== -1 ? index : 0);

    const conferences = eventDetail?.conferences ?? [];
    return [index, currentActiveId, conferences];
  }, [eventDetail]);
  const [currentEventDateTab, setCurrentEventDateTab] = useState(
    eventDetail?.eventDetailDates[todayIndex > -1 ? todayIndex : 0]?.eventDate,
  );
  const handleEventDateTabChange = (eventDate: any) => {
    setCurrentEventDateTab(eventDate.eventDate);
    onClickOfEventDateTabTracker({
      eventKey: eventDetail?.eventKey,
      eventDate: eventDate.eventDate,
    });
  };
  const [currentFilter, setCurrentFilter] = useState(FilterConferenceOptions.all);
  useEffect(() => {
    const currentDate = new Date();
    let conferences: any = [];
    if (currentFilter === FilterConferenceOptions.upcoming) {
      conferences = allConferences
        ?.filter(
          (c: IAgendaConference) =>
            currentEventDateTab ===
              new Date(c.start).toLocaleString('en-us', { dateStyle: 'medium' }) &&
            new Date(c.start) > currentDate,
        )
        ?.map((c: IAgendaConference) => c.conferenceKey);
    } else if (currentFilter === FilterConferenceOptions.ended) {
      conferences = allConferences
        ?.filter(
          (c: IAgendaConference) =>
            currentEventDateTab ===
              new Date(c.end).toLocaleString('en-us', { dateStyle: 'medium' }) &&
            new Date(c.end) < currentDate,
        )
        ?.map((c: IAgendaConference) => c.conferenceKey);
    } else if (currentFilter === FilterConferenceOptions.ongoing) {
      conferences = allConferences
        ?.filter(
          (c: IAgendaConference) =>
            currentEventDateTab ===
              new Date(c.end).toLocaleString('en-us', { dateStyle: 'medium' }) &&
            new Date(c.start) <= currentDate &&
            new Date(c.end) > currentDate,
        )
        ?.map((c: IAgendaConference) => c.conferenceKey);
    }
    setConferencesToShow(
      conferences.length
        ? conferences
        : allConferences.map((c: IAgendaConference) => c.conferenceKey),
    );
    if (!conferences.length) {
      setCurrentFilter(FilterConferenceOptions.all);
    }
  }, [currentEventDateTab, allConferences, currentFilter]);

  const isEndedOptionDisabled = () => {
    const currentDate = new Date();
    return allConferences
      ?.filter(
        (c: IAgendaConference) =>
          currentEventDateTab ===
            new Date(c.end).toLocaleString('en-us', { dateStyle: 'medium' }) &&
          new Date(c.end) < currentDate,
      )
      ?.map((c: IAgendaConference) => c.conferenceKey).length;
  };

  const isUpcomingOptionDisabled = () => {
    const currentDate = new Date();
    return allConferences
      ?.filter((c: IAgendaConference) => {
        return (
          currentEventDateTab ===
            new Date(c.start).toLocaleString('en-us', { dateStyle: 'medium' }) &&
          new Date(c.start) > currentDate
        );
      })
      ?.map((c: IAgendaConference) => c.conferenceKey).length;
  };

  const isOngoingOptionDisabled = () => {
    const currentDate = new Date();
    return allConferences
      ?.filter((c: IAgendaConference) => {
        return (
          currentEventDateTab ===
            new Date(c.start).toLocaleString('en-us', { dateStyle: 'medium' }) &&
          new Date(c.start) <= currentDate &&
          new Date(c.end) > currentDate
        );
      })
      ?.map((c: IAgendaConference) => c.conferenceKey).length;
  };

  const getDisabledStatusState = () => ({
    favorite: !getFavorites().length,
    ended: !isEndedOptionDisabled(),
    upcoming: !isUpcomingOptionDisabled(),
    ongoing: !isOngoingOptionDisabled(),
  });

  const [conferenceToShow, setConferencesToShow] = useState<string[]>(
    allConferences.map((c: IAgendaConference) => c.conferenceKey),
  );
  const numberOfUpcomingEvent = useMemo(() => {
    const currentDate = new Date();
    return (
      allConferences
        ?.filter((c: IAgendaConference) => new Date(c.start) > currentDate)
        ?.map((c: IAgendaConference) => c.conferenceKey).length ?? 0
    );
  }, [allConferences]);

  const numberOfEndedEvent = useMemo(() => {
    const currentDate = new Date();
    return (
      allConferences
        ?.filter((c: IAgendaConference) => new Date(c.end) < currentDate)
        ?.map((c: IAgendaConference) => c.conferenceKey).length ?? 0
    );
  }, [allConferences]);

  const numberOfOngoingEvent = useMemo(() => {
    const currentDate = new Date();
    return (
      allConferences
        ?.filter(
          (c: IAgendaConference) =>
            new Date(c.start) <= currentDate && new Date(c.end) > currentDate,
        )
        ?.map((c: IAgendaConference) => c.conferenceKey).length ?? 0
    );
  }, [allConferences]);

  const [optionStatusDisabled, setOptionStatusDisabled] = useState<OptionsStatusDisabled>({
    favorite: false,
    ended: false,
    upcoming: false,
    ongoing: false,
  });
  const filterOptionFormat = (optionName: string, number: number) => `${optionName} (${number})`;
  return (
    <div className="agenda">
      <Tabs
        className="agenda-tab"
        /*
          TODO: Remove ts-ignore once Chameleon-web export all parameters
          // @ts-ignore */
        activeindicator={false}
        activeid={activeId}
      >
        <AgendaHeader
          eventDetailDates={eventDetail?.eventDetailDates ?? []}
          handleEventDateTabChange={handleEventDateTabChange}
        />
        {eventDetail?.eventDetailDates?.map((eventDetailDate: IEventDetailDate) => {
          const conferenceToShowForThisDate = eventDetailDate.conferences.filter(
            (agendaConference: IAgendaConference) =>
              conferenceToShow.includes(agendaConference.conferenceKey),
          );
          return (
            <TabPanel key={eventDetailDate.eventDate} className="agenda-tab-panel">
              {userDetail && (
                <div className="agenda-filter">
                  <label htmlFor="favorites" className="agenda-filter-label">
                    <Typography variant="caption-default-02" color="text-02">
                      {t('agenda.filter')}
                    </Typography>
                  </label>
                  <Select
                    /*
                      TODO: Remove ts-ignore once Chameleon-web export all parameters
                      // @ts-ignore */
                    position="bottom"
                    ref={SelectRef}
                    inline
                    value={currentFilter}
                    className="agenda-select-filter"
                    name="favorites"
                    id="favorites"
                    onClick={(e) => {
                      setOptionStatusDisabled(getDisabledStatusState());
                      e.stopPropagation();
                    }}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                      const optionValue = e.target.value;
                      setCurrentFilter(optionValue);
                      const currentDate = new Date();
                      onClickOfEventFilterTracker({ optionValue, eventKey: eventDetail.eventKey });
                      switch (optionValue) {
                        case FilterConferenceOptions.all:
                          setConferencesToShow(
                            allConferences.map((c: IAgendaConference) => c.conferenceKey),
                          );
                          break;
                        /* Favorite Disabled
                        case FilterConferenceOptions.favorites:
                          setConferencesToShow(getFavorites());
                          break;
                           */
                        case FilterConferenceOptions.upcoming: {
                          const upcomingConferences = allConferences
                            ?.filter(
                              (c: IAgendaConference) =>
                                currentEventDateTab ===
                                  new Date(c.start).toLocaleString('en-us', {
                                    dateStyle: 'medium',
                                  }) && new Date(c.start) > currentDate,
                            )
                            ?.map((c: IAgendaConference) => c.conferenceKey);
                          setConferencesToShow(
                            upcomingConferences.length
                              ? upcomingConferences
                              : allConferences.map((c: IAgendaConference) => c.conferenceKey),
                          );
                          break;
                        }
                        case FilterConferenceOptions.ongoing: {
                          const ongoingConferences = allConferences
                            ?.filter(
                              (c: IAgendaConference) =>
                                currentEventDateTab ===
                                  new Date(c.start).toLocaleString('en-us', {
                                    dateStyle: 'medium',
                                  }) &&
                                new Date(c.start) <= currentDate &&
                                new Date(c.end) > currentDate,
                            )
                            ?.map((c: IAgendaConference) => c.conferenceKey);
                          setConferencesToShow(
                            ongoingConferences.length
                              ? ongoingConferences
                              : allConferences.map((c: IAgendaConference) => c.conferenceKey),
                          );
                          break;
                        }
                        case FilterConferenceOptions.ended: {
                          const endedConferences = allConferences
                            ?.filter(
                              (c: IAgendaConference) =>
                                currentEventDateTab ===
                                  new Date(c.end).toLocaleString('en-us', {
                                    dateStyle: 'medium',
                                  }) && new Date(c.end) < currentDate,
                            )
                            ?.map((c: IAgendaConference) => c.conferenceKey);
                          setConferencesToShow(
                            endedConferences.length
                              ? endedConferences
                              : allConferences.map((c: IAgendaConference) => c.conferenceKey),
                          );
                          break;
                        }
                        default:
                          setConferencesToShow(
                            allConferences.map(
                              ({ conferenceKey }: { conferenceKey: string }) => conferenceKey,
                            ),
                          );
                      }
                    }}
                  >
                    <Option value={FilterConferenceOptions.all}>
                      {filterOptionFormat(t('agenda.all'), allConferences?.length ?? 0)}
                    </Option>
                    <Option
                      value={FilterConferenceOptions.upcoming}
                      disabled={optionStatusDisabled.upcoming}
                    >
                      {filterOptionFormat(t('agenda.upcoming'), numberOfUpcomingEvent)}
                    </Option>
                    <Option
                      value={FilterConferenceOptions.ongoing}
                      disabled={optionStatusDisabled.ongoing}
                    >
                      {filterOptionFormat(t('agenda.ongoing'), numberOfOngoingEvent)}
                    </Option>
                    <Option
                      value={FilterConferenceOptions.ended}
                      disabled={optionStatusDisabled.ended}
                    >
                      {filterOptionFormat(t('agenda.finished'), numberOfEndedEvent)}
                    </Option>
                  </Select>
                </div>
              )}
              <AgendaDetail
                conferenceList={conferenceToShowForThisDate}
                handleEventSpeakerChange={handleEventSpeakerChange}
                userDetail={userDetail}
                eventKey={eventDetail?.eventKey}
                isMobOrTabView={isMobOrTabView}
              />
            </TabPanel>
          );
        })}
      </Tabs>
    </div>
  );
};

export default Agenda;
